import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
const domain = import.meta.env.VITE_API_DOMAIN;

export function useStreamChatResponse(proposal_id, purpose, searchType) {
  const [data, setData] = useState("");
  const [isDone, setIsDone] = useState(false);
  const queryClient = useQueryClient();
  const thread_id = queryClient.getQueryData([
    "chatHistory",
    purpose,
    searchType,
    proposal_id,
  ])?.thread_id;
  const assistant_id = queryClient.getQueryData([
    "chatHistory",
    purpose,
    searchType,
    proposal_id,
  ])?.assistant_id;
  const vector_store_id = queryClient.getQueryData([
    "chatHistory",
    purpose,
    searchType,
    proposal_id,
  ])?.vector_store_id;
  const fetchData = async ({ proposal_id, question, context = null }) => {
    let prompt = "";
    if (context)
      prompt = `Given the context: ${context} and the question: ${question}, please answer the question.`;
    else prompt = question;
    setIsDone(false);
    const response = await fetch(`${domain}/chat/ask-question`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        question: prompt,
        thread_id,
        assistant_id,
        vector_store_id,
        proposal_id,
        purpose,
        search_type: searchType,
      }),
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    return reader.read().then(function processText({ done, value }) {
      if (done) {
        setIsDone(true);
        return;
      }
      const chunk = decoder.decode(value, { stream: true });
      setData((prevData) => prevData + chunk);
      return reader.read().then(processText);
    });
  };

  const mutation = useMutation({
    mutationFn: fetchData,
    onError: () => {
      queryClient.invalidateQueries([
        "chatHistory",
        purpose,
        searchType,
        proposal_id,
      ]);
    },
    onSuccess: async () => {
      // Update cache with streaming data
      await queryClient.cancelQueries([
        "chatHistory",
        purpose,
        searchType,
        proposal_id,
      ]);
      queryClient.setQueryData(
        ["chatHistory", purpose, searchType, proposal_id],
        (oldData) => {
          const updatedData = { ...oldData };
          const lastMessageIndex = updatedData.chat_history.length - 1;

          updatedData.chat_history[lastMessageIndex].content = data;
          return updatedData;
        }
      );
      setData("");
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries([
        "chatHistory",
        purpose,
        searchType,
        variables.proposal_id,
      ]);

      queryClient.setQueryData(
        ["chatHistory", purpose, searchType, variables.proposal_id],
        (oldData) => ({
          ...oldData,
          chat_history: [
            ...oldData.chat_history,
            {
              role: "user",
              content: variables.question,
            },
            {
              role: "assistant",
              content: "Thinking...",
            },
          ],
        })
      );
    },
  });

  return {
    ...mutation,
    streamingData: data,
    streamingDone: isDone,
  };
}
